import {
  Box,
  Button,
  Center,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
  Select,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import * as React from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { UseFormReturn, useFieldArray, useWatch } from 'react-hook-form';

import {
  EImageGroupImageAlignment,
  EPageContentBackgroundColor,
} from '@/graphql-types/globalTypes';
import { Card } from '@/imports/ui/chakra/components/Card';
import { PlusIcon } from '@/imports/ui/chakra/feather';
import { BackButton } from '@/modules/website-builder/components/BackButton';
import { DEFAULT_IMAGE_GROUP_IMAGE } from '@/modules/website-builder/default-content';
import { WebsiteBuilderPageFormValues } from '@/modules/website-builder/types';

import { ImageEditor } from './components/ImageEditor';
import { ImageGroupImageItem } from './components/ImageGroupImageItem';

const remapExpandedIndexesOnMove = (
  expandedIndexes: number[],
  from: number,
  to: number
) => {
  return expandedIndexes.map((index) => {
    if (index === from) {
      return to;
    }

    if (from < to && index > from && index <= to) {
      return index - 1;
    }

    if (from > to && index >= to && index < from) {
      return index + 1;
    }

    return index;
  });
};

const remapExpandedIndexesOnRemove = (
  expandedIndexes: number[],
  removedIndex: number
) => {
  return expandedIndexes
    .filter((index) => index !== removedIndex)
    .map((index) => (index > removedIndex ? index - 1 : index));
};

export const ImageGroupEditor: React.FC<{
  index: number;
  defaultName: string;
  onClickBack: () => void;
  methods: UseFormReturn<WebsiteBuilderPageFormValues, any>;
}> = (props) => {
  const newImageModal = useDisclosure();
  const { control, register } = props.methods;

  const imagesFieldArray = useFieldArray({
    control,
    name: `content.${props.index}.images`,
  });

  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const [expandedIndexes, setExpandedIndexes] = React.useState<number[]>([]);

  const imageMaxHeight = useWatch({
    control,
    name: `content.${props.index}.options.imageHeight`,
  });

  return (
    <Stack spacing="4">
      <Stack spacing="4">
        <BackButton onClick={props.onClickBack}>
          Back to all components
        </BackButton>

        <Card as={Stack} spacing="4" p="6">
          <Text textStyle="bodyStrong">Component Settings</Text>

          <FormControl>
            <FormLabel>Name</FormLabel>
            <Input
              placeholder="Image Group"
              {...register(`content.${props.index}.name`)}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Background Color</FormLabel>
            <Select
              {...register(`content.${props.index}.options.backgroundColor`)}
            >
              <option value={EPageContentBackgroundColor.WHITE}>White</option>
              <option value={EPageContentBackgroundColor.TRANSPARENT}>
                Light Gray
              </option>
            </Select>
          </FormControl>
        </Card>

        <Card as={Stack} spacing="4" p="6">
          <Text textStyle="bodyStrong">Image Settings</Text>

          <FormControl>
            <FormLabel>Height</FormLabel>
            <InputGroup>
              <Input
                {...register(`content.${props.index}.options.imageHeight`, {
                  valueAsNumber: true,
                })}
              />
              <InputRightAddon>px</InputRightAddon>
            </InputGroup>
          </FormControl>

          <FormControl>
            <FormLabel>Alignment</FormLabel>
            <Select
              {...register(`content.${props.index}.options.imageAlignment`)}
            >
              <option value={EImageGroupImageAlignment.CENTER}>Center</option>
              <option value={EImageGroupImageAlignment.LEFT}>Left</option>
            </Select>
          </FormControl>
        </Card>

        <DragDropContext
          onDragEnd={(result) => {
            if (!result.destination) {
              return;
            }

            imagesFieldArray.move(
              result.source.index,
              result.destination!.index
            );
            setExpandedIndexes((current) =>
              remapExpandedIndexesOnMove(
                current,
                result.source.index,
                result.destination!.index
              )
            );
          }}
        >
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <Box ref={provided.innerRef} {...provided.droppableProps}>
                {imagesFieldArray.fields.map((field, index) => {
                  return (
                    <ImageGroupImageItem
                      key={index}
                      parentIndex={props.index}
                      index={index}
                      imageProxy={field.imageProxy}
                      isExpanded={expandedIndexes.includes(index)}
                      onToggleExpand={() => {
                        setExpandedIndexes((current) =>
                          current.includes(index)
                            ? current.filter(
                                (expandedIndex) => expandedIndex !== index
                              )
                            : [...current, index]
                        );
                      }}
                      onEdit={() => setActiveIndex(index)}
                      onDelete={() => {
                        imagesFieldArray.remove(index);
                        setExpandedIndexes((current) =>
                          remapExpandedIndexesOnRemove(current, index)
                        );
                      }}
                      methods={props.methods}
                      pointerEvents={
                        snapshot.isDraggingOver ? 'none' : undefined
                      }
                    />
                  );
                })}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </Stack>

      {imagesFieldArray.fields.map((field, index) => {
        return activeIndex === index ? (
          <ImageEditor
            key={field.id}
            onClickBack={() => setActiveIndex(null)}
            imageMaxHeight={imageMaxHeight}
            value={{
              ...field,
              ...props.methods.getValues(
                `content.${props.index}.images.${index}`
              ),
            }}
            onChange={(value) => {
              imagesFieldArray.update(index, {
                ...props.methods.getValues(
                  `content.${props.index}.images.${index}`
                ),
                ...value,
              });
              setActiveIndex(null);
            }}
            nonDraftImageProxy={field.imageProxy}
          />
        ) : null;
      })}

      {newImageModal.isOpen && (
        <ImageEditor
          onClickBack={newImageModal.onClose}
          imageMaxHeight={imageMaxHeight}
          value={{ ...DEFAULT_IMAGE_GROUP_IMAGE }}
          onChange={(value) => {
            imagesFieldArray.append(value);
            newImageModal.onClose();
          }}
          nonDraftImageProxy={null}
        />
      )}

      <Center>
        <Button
          colorScheme="purple"
          leftIcon={<PlusIcon />}
          size="sm"
          onClick={newImageModal.onOpen}
        >
          Add New Image
        </Button>
      </Center>
    </Stack>
  );
};
